import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { authApi } from '../services/api';

interface AuthStore {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login(email, password);
          // Response format: { success: true, data: { user: {...}, token: "..." } }
          const responseData = response.data?.data || response.data;
          const { token, user } = responseData;
          
          localStorage.setItem('token', token);
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          
          return true;
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Login failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          return false;
        }
      },

      logout: () => {
        const token = localStorage.getItem('token');
        if (token) {
          authApi.logout().catch((error) => {
            console.warn('Logout request failed', error);
          });
        }
        localStorage.removeItem('token');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
      },

      checkAuth: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        try {
          const response = await authApi.getMe();
          // Response format: { success: true, data: { user: {...} } }
          const userData = response.data?.data?.user || response.data?.user || response.data;
          set({
            user: userData,
            token,
            isAuthenticated: true,
          });
        } catch (error: any) {
          // Only log out on a genuine auth rejection (401). A timeout or network
          // blip (e.g. the dev DB being slow, or the OS pausing the tab on an
          // app-switch) must NOT drop the session — that was logging users out
          // "too quickly" and resetting their patient screen. Keep the persisted
          // session in those cases; the next successful call re-confirms it.
          const status = error?.response?.status;
          if (status === 401) {
            localStorage.removeItem('token');
            set({
              user: null,
              token: null,
              isAuthenticated: false,
            });
          } else {
            set({
              token,
              isAuthenticated: true,
              user: get().user,
            });
          }
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      // Persist isAuthenticated along with token and user for proper state restoration
      partialize: (state) => ({ 
        token: state.token, 
        user: state.user, 
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);

